/** This class represents a generic Order Form component used for order creation and editing.
 *
 * The actual order form being displayed is contingent on the platform being used
 * which is realized via the OrderFormChooser component. Do not import anything from this file
 * from inside the individual order form components to prevent circular dependencies.
 */
import { useMemo } from 'react';
import { Dialog } from '@mui/material';
import { SamdBreastOrderForm } from '@samd/features/orders/breast_form';
import { DemoSamdProstateBiopsyOrderForm } from '@samd/features/orders/demo_prostate_biopsy_form';
import { UkSamdProstateBiopsyOrderForm } from '@samd/features/orders/uk_prostate_biopsy_form';
import { UkSamdProstatePostRpOrderForm } from '@samd/features/orders/uk_prostate_post_rp_form';
import { UsSamdProstateBiopsyOrderForm } from '@samd/features/orders/us_prostate_biopsy_form';
import { LdtOrderFormDialog } from '@ldt/features/orders';
import { LdtMetastaticProstateClinicOrderForm } from '@ldt/features/orders/metastatic_prostate_clinic_form';
import { LdtMetastaticProstateTempusOrderForm } from '@ldt/features/orders/metastatic_prostate_tempus_form';
import { LdtProstateBiopsyOrderForm } from '@ldt/features/orders/prostate_biopsy_form';
import { LdtProstatePostRPOrderForm } from '@ldt/features/orders/prostate_post_rp_form';
import { OrderRepository } from 'src/api/order_repository';
import { getApp, getPlatform } from 'src/config/environment';
import { useWorkflowType } from 'src/distribution_platforms/features/hooks/use_workflow';
import { OrderBaseFieldsFragment, OrderStatus, SpecimenType } from 'src/types';
import { App } from 'src/types/app';
import { DistributionPlatform } from 'src/types/env';
import { WorkflowType } from 'src/types/workflows';

type OrderFormProps<
  TOrder extends OrderBaseFieldsFragment = OrderBaseFieldsFragment,
> = {
  orderRepository: OrderRepository;
  initialOrder: TOrder;
  onClose: () => void;
  workflowType: WorkflowType;
};

interface OrderFormStrategy {
  component: React.ComponentType<OrderFormProps>;
}

/**
 * Registers a platform form whose `initialOrder` is a platform-specific fragment
 * under the shared chooser prop shape. Safe because the chooser only mounts a
 * form for the matching app/specimen/workflow with that platform's orders.
 */
function asOrderFormComponent<TOrder extends OrderBaseFieldsFragment>(
  component: React.ComponentType<OrderFormProps<TOrder>>,
): React.ComponentType<OrderFormProps> {
  return component as React.ComponentType<OrderFormProps>;
}

// Strategy implementations
const orderFormStrategies: Record<string, OrderFormStrategy> = {
  [`${App.Ldt}-${SpecimenType.ProstateBiopsy}`]: {
    component: asOrderFormComponent(LdtProstateBiopsyOrderForm),
  },
  [`${App.Ldt}-${SpecimenType.ProstatePostRp}`]: {
    component: asOrderFormComponent(LdtProstatePostRPOrderForm),
  },
  [`${App.Ldt}-${SpecimenType.MetastaticProstateBiopsy}-${WorkflowType.TEMPUS}`]:
    {
      component: asOrderFormComponent(LdtMetastaticProstateTempusOrderForm),
    },
  [`${App.Ldt}-${SpecimenType.MetastaticProstateBiopsy}-${WorkflowType.CLINIC}`]:
    {
      component: asOrderFormComponent(LdtMetastaticProstateClinicOrderForm),
    },
  [`${App.Ldt}-${SpecimenType.MetastaticProstateBiopsy}-${WorkflowType.ANCILLARY_LAB}`]:
    {
      component: asOrderFormComponent(LdtMetastaticProstateClinicOrderForm),
    },
  [`${App.SamdDemo}-${SpecimenType.ProstateBiopsy}`]: {
    component: asOrderFormComponent(DemoSamdProstateBiopsyOrderForm),
  },
  [`${App.SamdUs}-${SpecimenType.ProstateBiopsy}`]: {
    component: asOrderFormComponent(UsSamdProstateBiopsyOrderForm),
  },
  [`${App.SamdUs}-${SpecimenType.BreastBiopsy}`]: {
    component: asOrderFormComponent(SamdBreastOrderForm),
  },
  [`${App.SamdUk}-${SpecimenType.ProstateBiopsy}`]: {
    component: asOrderFormComponent(UkSamdProstateBiopsyOrderForm),
  },
  [`${App.SamdUk}-${SpecimenType.BreastBiopsy}`]: {
    component: asOrderFormComponent(SamdBreastOrderForm),
  },
  [`${App.SamdUk}-${SpecimenType.ProstatePostRp}`]: {
    component: asOrderFormComponent(UkSamdProstatePostRpOrderForm),
  },
  [`${App.SamdEu}-${SpecimenType.ProstateBiopsy}`]: {
    component: asOrderFormComponent(UkSamdProstateBiopsyOrderForm),
  },
  [`${App.SamdEu}-${SpecimenType.BreastBiopsy}`]: {
    component: asOrderFormComponent(SamdBreastOrderForm),
  },
};

// Strategy selector
function getOrderFormStrategy(
  app: App,
  specimenType: SpecimenType,
  workflowType: WorkflowType,
): OrderFormStrategy {
  // Try workflow-specific key first, then fall back to the generic key
  const workflowKey = `${app}-${specimenType}-${workflowType}`;
  const genericKey = `${app}-${specimenType}`;
  const strategy =
    orderFormStrategies[workflowKey] ?? orderFormStrategies[genericKey];

  if (!strategy) {
    throw new Error(
      `Order form not supported for app: ${app}, specimen type: ${specimenType}`,
    );
  }

  return strategy;
}

export const OrderFormChooser = ({
  order,
  testType,
  onClose,
  orderRepository,
}: {
  order?: OrderBaseFieldsFragment;
  testType?: SpecimenType;
  onClose: () => void;
  orderRepository: OrderRepository;
}) => {
  if (order && testType) {
    throw new Error(
      'Cannot provide both order and testType. Should only provide testType when creating a new order.',
    );
  }

  const specimenType = order?.specimenType ?? testType;
  if (!specimenType) {
    throw new Error('Either order or testType must be provided');
  }

  // Create initial order for new orders
  const initialOrder =
    order ||
    ({
      specimenType: specimenType,
      status: OrderStatus.Draft,
    } as OrderBaseFieldsFragment);

  // Get the appropriate strategy
  const workflowType = useWorkflowType();
  const strategy = getOrderFormStrategy(getApp(), specimenType, workflowType);
  const Component = strategy.component;

  return (
    <Component
      key={initialOrder.id}
      orderRepository={orderRepository}
      initialOrder={initialOrder}
      onClose={onClose}
      workflowType={workflowType}
    />
  );
};

export const ModalOrderForm = ({
  isOpen,
  order,
  testType,
  onClose,
  orderRepository,
}: {
  isOpen: boolean;
  order?: OrderBaseFieldsFragment;
  testType?: SpecimenType;
  onClose: () => void;
  orderRepository: OrderRepository;
}) => {
  const memoizedOrderFormChooser = useMemo(() => {
    return (
      <OrderFormChooser
        order={order}
        testType={testType}
        onClose={onClose}
        orderRepository={orderRepository}
      />
    );
  }, [order, testType, onClose, orderRepository]);

  if (getPlatform() === DistributionPlatform.Ldt) {
    return (
      <LdtOrderFormDialog
        open={isOpen}
        onClose={onClose}
        maxWidth={false}
        fullScreen
      >
        {memoizedOrderFormChooser}
      </LdtOrderFormDialog>
    );
  } else if (getPlatform() === DistributionPlatform.Samd) {
    return (
      <Dialog open={isOpen} onClose={onClose} maxWidth={false}>
        {memoizedOrderFormChooser}
      </Dialog>
    );
  }
};
